import { ClassAttributes, FC, ReactNode, useEffect, useRef } from 'react';
import type Konva from 'konva';
import {
  Circle,
  Group,
  Image as KonvaImage,
  Rect,
  Text,
  Transformer,
} from 'react-konva';
import type { KonvaNodeEvents } from 'react-konva';
import {
  LayoutFontStyle,
  LayoutObject as LayoutObjectType,
} from '../../../../../types/types';
import { useLayoutImage } from '../../../../../hooks/useLayoutImage';
import { LayoutFixedCell } from '../../../../../utils/layouts/getLayoutFixedCell';
import { fontStyleToKonva } from '../../../../../utils/layouts/fontStyleToKonva';
import { getLayoutObjectParam } from '../../../../../utils/layouts/getLayoutObjectParam';
import { buttonHAlignToKonva } from '../../../../../utils/layouts/buttonHAlignToKonva';
import { buttonVAlignToKonva } from '../../../../../utils/layouts/buttonVAlignToKonva';
import { getLayoutButtonIndent } from '../../../../../utils/layouts/getLayoutButtonIndent';
import { getLayoutIndentOverlayRects } from '../../../../../utils/layouts/getLayoutIndentOverlayRects';
import { getLayoutImagePlacement } from '../../../../../utils/layouts/getLayoutImagePlacement';
import { useTransformerWindowBinding } from '../../../../../hooks/useTransformerWindowBinding';
import { useLayoutObjectHandlers } from '../../hooks/useLayoutObjectHandlers';
import { clampRectToCanvas } from '../../../../../utils/layouts/clampRectToCanvas';
import { getDragBoundPosition } from './utils/getDragBoundPosition';
import { getEnabledAnchors } from './utils/getEnabledAnchors';
import { resolveTransformerBox } from './utils/resolveTransformerBox';
import {
  LAYOUT_OBJECT_BADGE_INSET,
  LAYOUT_OBJECT_BADGE_RADIUS,
  LAYOUT_OBJECT_CHANGED_BADGE_COLOR,
  LAYOUT_OBJECT_COPIED_DASH,
  LAYOUT_OBJECT_COPIED_FRAME_OFFSET,
  LAYOUT_OBJECT_COPIED_FRAME_STROKE_WIDTH,
  LAYOUT_OBJECT_COPIED_STROKE,
  LAYOUT_OBJECT_DEFAULT_BACKGROUND,
  LAYOUT_OBJECT_DEFAULT_FONT_COLOR,
  LAYOUT_OBJECT_DEFAULT_FONT_FAMILY,
  LAYOUT_OBJECT_DEFAULT_FONT_SIZE,
  LAYOUT_OBJECT_DEFAULT_STROKE,
  LAYOUT_OBJECT_INDENT_OVERLAY_FILL,
  LAYOUT_OBJECT_DEFAULT_STROKE_WIDTH,
  LAYOUT_OBJECT_MIN_SIZE,
  LAYOUT_OBJECT_NEW_BADGE_COLOR,
  LAYOUT_OBJECT_SELECTED_STROKE,
  LAYOUT_OBJECT_SELECTED_STROKE_WIDTH,
} from '../../../../../constants';

const KonvaGroup = Group as FC<
  Konva.GroupConfig &
    KonvaNodeEvents &
    ClassAttributes<Konva.Group> & { children?: ReactNode }
>;

interface LayoutObjectProps {
  object: LayoutObjectType;
  index: number;
  isSelected: boolean;
  canvasWidth: number;
  canvasHeight: number;
  snapEnabled: boolean;
  gridX: number;
  gridY: number;
  gapX: number;
  gapY: number;
  scaleX: number;
  scaleY: number;
  // Fixed mode: the grid cell replaces the object geometry (index-derived).
  fixedCell?: LayoutFixedCell;
  // Fixed mode: canvas-level default styling of the buttons — an object's own
  // values win, the FE constants stay the last resort.
  styleDefaults?: {
    backgroundColor?: string;
    fontColor?: string;
    fontFamily?: string;
    fontSize?: number;
    fontStyle?: LayoutFontStyle[];
  };
  // Free mode: drag moves the object; fixed mode: drag reorders the list.
  isDraggable: boolean;
  isResizable: boolean;
  // The window the canvas currently renders in when popped out — the
  // transformer must mirror its anchor-drag events from there (Konva binds
  // them on the main window only). Null/undefined = canvas in the main window.
  eventWindow?: Window | null;
  isDirty: boolean;
  isNew: boolean;
  isCopied: boolean;
  focusedField?: string | null;
  onSelect: (index: number) => void;
  onChange: (index: number, patch: Partial<LayoutObjectType>) => void;
  onReorderDrop?: (index: number, centerX: number, centerY: number) => void;
}

const LayoutObject: FC<LayoutObjectProps> = ({
  object,
  index,
  isSelected,
  canvasWidth,
  canvasHeight,
  snapEnabled,
  gridX,
  gridY,
  gapX,
  gapY,
  scaleX,
  scaleY,
  fixedCell,
  styleDefaults,
  isDraggable,
  isResizable,
  eventWindow,
  isDirty,
  isNew,
  isCopied,
  focusedField,
  onSelect,
  onChange,
  onReorderDrop,
}) => {
  const groupRef = useRef<Konva.Group>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  // Item bitmap (base64 from the API); backgroundImageMethod decides the
  // placement, overflow past the box is clipped.
  const backgroundImage = useLayoutImage(object.backgroundImage);

  const x = fixedCell?.x ?? object.x ?? 0;
  const y = fixedCell?.y ?? object.y ?? 0;
  const width = fixedCell?.width ?? object.width ?? 0;
  const height = fixedCell?.height ?? object.height ?? 0;
  const font = fontStyleToKonva(object.fontStyle ?? styleDefaults?.fontStyle);
  // Caption alignment/indent item parameters — the keys come from the
  // definition and keep the desktop Septim casing (BUTTONHALIGN), hence the
  // case-insensitive reads.
  const captionAlign = buttonHAlignToKonva(
    getLayoutObjectParam(object, 'buttonhalign')
  );
  const captionVerticalAlign = buttonVAlignToKonva(
    getLayoutObjectParam(object, 'buttonvalign')
  );
  const captionHIndent = getLayoutButtonIndent(
    getLayoutObjectParam(object, 'buttonhindent')
  );
  const captionVIndent = getLayoutButtonIndent(
    getLayoutObjectParam(object, 'buttonvindent')
  );
  const indentOverlayRects = focusedField
    ? getLayoutIndentOverlayRects({
        field: focusedField,
        width,
        height,
        hIndent: captionHIndent,
        vIndent: captionVIndent,
      })
    : [];
  const imagePlacement = backgroundImage
    ? getLayoutImagePlacement({
        method: getLayoutObjectParam(object, 'backgroundimagemethod'),
        imageWidth: backgroundImage.naturalWidth,
        imageHeight: backgroundImage.naturalHeight,
        boxWidth: width,
        boxHeight: height,
      })
    : null;
  const maxX = Math.max(0, canvasWidth - width);
  const maxY = Math.max(0, canvasHeight - height);
  const enabledAnchors = getEnabledAnchors({
    x,
    y,
    width,
    height,
    canvasWidth,
    canvasHeight,
  });

  // Non-uniform zoom must not deform glyphs (or the badge): they counter-scale
  // to the uniform min(scale) factor; the text box stretches back so it still
  // covers the whole object area.
  const uniformScale = Math.min(scaleX, scaleY);
  const counterScaleX = uniformScale / scaleX;
  const counterScaleY = uniformScale / scaleY;

  useTransformerWindowBinding(
    transformerRef,
    isSelected && isResizable ? (eventWindow ?? null) : null
  );

  const { handleSelect, handleDragStart, handleDragEnd, handleTransformEnd } =
    useLayoutObjectHandlers({
      index,
      x,
      y,
      width,
      height,
      snapEnabled,
      gridX,
      gridY,
      gapX,
      gapY,
      fixedCell,
      groupRef,
      canvasWidth,
      canvasHeight,
      onSelect,
      onChange,
      onReorderDrop,
    });

  useEffect(() => {
    if (
      !isSelected ||
      !isResizable ||
      !groupRef.current ||
      !transformerRef.current
    ) {
      return;
    }

    transformerRef.current.nodes([groupRef.current]);
    transformerRef.current.forceUpdate();
    transformerRef.current.getLayer()?.batchDraw();
  }, [x, y, width, height, isSelected, isResizable]);

  return (
    <>
      <KonvaGroup
        ref={groupRef}
        x={x}
        y={y}
        draggable={isDraggable}
        dragBoundFunc={(position): { x: number; y: number } =>
          getDragBoundPosition({
            position,
            snapEnabled,
            gridX,
            gridY,
            gapX,
            gapY,
            scaleX,
            scaleY,
            maxX,
            maxY,
          })
        }
        onClick={handleSelect}
        onTap={handleSelect}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
      >
        <Rect
          width={width}
          height={height}
          // `||`: the API sends an unassigned color as an empty string — an
          // empty fill would paint nothing (invisible button).
          fill={
            object.backgroundColor ||
            styleDefaults?.backgroundColor ||
            LAYOUT_OBJECT_DEFAULT_BACKGROUND
          }
          stroke={
            isSelected
              ? LAYOUT_OBJECT_SELECTED_STROKE
              : LAYOUT_OBJECT_DEFAULT_STROKE
          }
          strokeWidth={
            isSelected
              ? LAYOUT_OBJECT_SELECTED_STROKE_WIDTH
              : LAYOUT_OBJECT_DEFAULT_STROKE_WIDTH
          }
        />
        {isCopied && (
          // Dashed ring around the Ctrl+C source (cleared by a canvas click) —
          // offset outside the box so the selection border doesn't cover it.
          <Rect
            x={-LAYOUT_OBJECT_COPIED_FRAME_OFFSET}
            y={-LAYOUT_OBJECT_COPIED_FRAME_OFFSET}
            width={width + 2 * LAYOUT_OBJECT_COPIED_FRAME_OFFSET}
            height={height + 2 * LAYOUT_OBJECT_COPIED_FRAME_OFFSET}
            stroke={LAYOUT_OBJECT_COPIED_STROKE}
            strokeWidth={LAYOUT_OBJECT_COPIED_FRAME_STROKE_WIDTH}
            dash={LAYOUT_OBJECT_COPIED_DASH}
            listening={false}
          />
        )}
        {backgroundImage && imagePlacement && (
          <Group
            clipX={0}
            clipY={0}
            clipWidth={width}
            clipHeight={height}
            listening={false}
          >
            <KonvaImage
              image={backgroundImage}
              x={imagePlacement.x}
              y={imagePlacement.y}
              width={imagePlacement.width}
              height={imagePlacement.height}
            />
          </Group>
        )}
        <Text
          x={captionHIndent}
          y={captionVIndent}
          width={Math.max(0, width - 2 * captionHIndent) / counterScaleX}
          height={Math.max(0, height - 2 * captionVIndent) / counterScaleY}
          scaleX={counterScaleX}
          scaleY={counterScaleY}
          text={object.caption ?? ''}
          fill={
            object.fontColor ||
            styleDefaults?.fontColor ||
            LAYOUT_OBJECT_DEFAULT_FONT_COLOR
          }
          fontFamily={
            object.fontFamily ||
            styleDefaults?.fontFamily ||
            LAYOUT_OBJECT_DEFAULT_FONT_FAMILY
          }
          fontSize={
            object.fontSize ||
            styleDefaults?.fontSize ||
            LAYOUT_OBJECT_DEFAULT_FONT_SIZE
          }
          fontStyle={font.fontStyle}
          textDecoration={font.textDecoration}
          align={captionAlign}
          verticalAlign={captionVerticalAlign}
          listening={false}
        />
        {indentOverlayRects.map((rect, rectIndex) => (
          // Indent zone of the focused indent form field, DevTools-padding style.
          <Rect
            key={rectIndex}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            fill={LAYOUT_OBJECT_INDENT_OVERLAY_FILL}
            listening={false}
          />
        ))}
        {isDirty && (
          <Circle
            x={width - LAYOUT_OBJECT_BADGE_INSET}
            y={LAYOUT_OBJECT_BADGE_INSET}
            scaleX={counterScaleX}
            scaleY={counterScaleY}
            radius={LAYOUT_OBJECT_BADGE_RADIUS}
            fill={
              isNew
                ? LAYOUT_OBJECT_NEW_BADGE_COLOR
                : LAYOUT_OBJECT_CHANGED_BADGE_COLOR
            }
            listening={false}
          />
        )}
      </KonvaGroup>
      {isSelected && isResizable && (
        <Transformer
          ref={transformerRef}
          rotateEnabled={false}
          keepRatio={false}
          enabledAnchors={enabledAnchors}
          boundBoxFunc={(oldBox, newBox) =>
            resolveTransformerBox({
              oldBox,
              newBox,
              scaleX,
              scaleY,
            })
          }
        />
      )}
    </>
  );
};

export default LayoutObject;
